import { useMemo } from 'react';
import { useEdges, useNodes } from '@xyflow/react';
import { Checkbox, Select } from 'antd';
import { CustomSelectIcon } from '@/assets/icons/icons';
import type { DelayReplyReceivedEvent } from '@/proto/logiblocks/logiblocks';
import type { ReactFlowEdge, ReactFlowNode } from '@/pages/Logiblocks/types';
import { getEarlierCommunicationNodes } from './delayUntilEventModel';
import styles from './DelayUntilEvent.module.css';

interface ReplySourceOption {
  value: string;
  label: string;
  channel: string;
}

/**
 * The communication nodes used earlier in this run, labelled by channel (and by
 * the node's own name when the author renamed it). Reads the graph through
 * React Flow, so it only works under a React Flow provider.
 */
function useReplySourceOptions(nodeId: string): ReplySourceOption[] {
  const nodes = useNodes<ReactFlowNode>();
  const edges = useEdges<ReactFlowEdge>();

  return useMemo(() => {
    const sources = getEarlierCommunicationNodes(nodeId, nodes, edges);
    const seen = new Map<string, number>();
    return sources.map(({ nodeId: sourceId, channel }) => {
      const node = nodes.find((n) => n.id === sourceId);
      const custom = node?.data.custom_display_name;
      const base = custom ? `${channel}: ${custom}` : channel;
      const count = (seen.get(base) ?? 0) + 1;
      seen.set(base, count);
      return {
        value: sourceId,
        channel,
        label: count > 1 ? `${base} (${count})` : base,
      };
    });
  }, [nodeId, nodes, edges]);
}

/** Channels of the earlier nodes a Reply received event listens to, for its card summary. */
export function useReplyChannels(
  nodeId: string,
  event: DelayReplyReceivedEvent
): string[] {
  const options = useReplySourceOptions(nodeId);
  return event.source_node_ids.map(
    (id) => options.find((o) => o.value === id)?.channel ?? 'Removed step'
  );
}

interface DelayReplyToSelectProps {
  nodeId: string;
  value: string[];
  onChange: (sourceNodeIds: string[]) => void;
  disabled?: boolean;
}

/**
 * "Reply to" picker: the communication nodes used earlier in this run. With
 * none, says so; saving with nothing selected means any reply on the shipment.
 */
export const DelayReplyToSelect = ({
  nodeId,
  value,
  onChange,
  disabled = false,
}: DelayReplyToSelectProps) => {
  const sourceOptions = useReplySourceOptions(nodeId);

  // A saved node that was since deleted still shows (and can be unticked).
  const options = useMemo(() => {
    const known = new Set(sourceOptions.map((o) => o.value));
    return [
      ...sourceOptions,
      ...value
        .filter((id) => !known.has(id))
        .map((id) => ({ value: id, label: 'Removed step', channel: '' })),
    ];
  }, [sourceOptions, value]);

  if (options.length === 0) {
    return (
      <div className={styles.emptyText}>
        No earlier step in this run sends an email, SMS, Slack, Teams or AI
        agent message. Any reply on the shipment will continue the run.
      </div>
    );
  }

  return (
    <Select
      mode='multiple'
      aria-label='Reply to'
      value={value}
      onChange={onChange}
      options={options}
      disabled={disabled}
      // The design's chevron, which a multi-select also keeps while open: antd would swap in a
      // magnifier.
      suffixIcon={<CustomSelectIcon />}
      placeholder='Select'
      className={`agentSelect agentSelect--darkText ${styles.multiSelect}`}
      style={{ width: '100%' }}
      optionRender={(option) => (
        <Checkbox checked={value.includes(String(option.value))}>
          {option.label}
        </Checkbox>
      )}
      menuItemSelectedIcon={null}
      maxTagCount='responsive'
    />
  );
};
